import { call, delay, put, takeEvery } from "redux-saga/effects";
import venueActions from "./actions";
import axios, { all } from "axios";
import { API_URL } from "../../utils/constants";
import commonActions from "../common/actions";

const venueSagas = function* () {
  yield all([
    yield takeEvery(venueActions.GET_CANDIDATE_COUNT, getCandidateCount),
    yield takeEvery(venueActions.UPLOAD_VENUE_DATA, createVenueData),
    yield takeEvery(venueActions.IMPORT_VENUE_DATA_API, importVenueDataFromApi),
    yield takeEvery(venueActions.GET_VENUE_DETAILS, getVenueDetails),
    yield takeEvery(
      venueActions.GET_VENUE_DISTRICT_DETAILS,
      getVenueDistrictDetails
    ),
    yield takeEvery(
      venueActions.GET_CATEGORIES_OF_COURSE,
      getCategoriesOfCourse
    ),
    
    yield takeEvery(venueActions.RUN_SEAT_ALLOCATION, runSeatAllocation),
    yield takeEvery(venueActions.GET_SEAT_ALLOCATION, getSeatAllocation),
    yield takeEvery(
      venueActions.GET_SEAT_ALLOCATION_DETAILS,
      getVenueseatAllocationDetails
    ),
    yield takeEvery(
      venueActions.GET_CANDIDATE_ALLOCATION_DEAILS,
      getCandidateAllocationDetails
    ),
    yield takeEvery(
      venueActions.RESET_SEAT_ALLOCATION,
      resetSeatAllocationDetails
    ),
    yield takeEvery(
      venueActions.STOP_SEAT_ALLOCATION,
      stopSeatAllocationDetails
    ),

    yield takeEvery(venueActions.UPLOAD_OTBS_VENUE, uploadOtbsVenue),
    yield takeEvery(venueActions.UPDATE_OTBS_VENUE, updateOtbsVenue),

    yield takeEvery(venueActions.OTBS_GET_VENUE, getOtbsVenue),
    yield takeEvery(venueActions.GET_ALLOCATION_STATUS, getSeatAllocationStatus),

    yield takeEvery(venueActions.GET_DUPLICATE_SUMMARY, getDuplicateSummary),
    yield takeEvery(venueActions.REFRESH_DUPLICATES, refreshDuplicates),
    yield takeEvery(venueActions.GET_DUPLICATE_GROUPS, getDuplicateGroups),
    yield takeEvery(venueActions.REVIEW_DUPLICATE_GROUP, reviewDuplicateGroup),
    yield takeEvery(venueActions.GET_DUPLICATE_REPORT, getDuplicateReport)
  ]);
};
const createVenueData = function* (data) {
  const { payload } = data;
  payload.setLoading(true);
  yield put({ type: commonActions.SET_PAGE_LOADER, payload: true });
  try {
    if (payload?.file && payload?.courseid) {
      let formData = new FormData();
      const documentFile = new File([payload?.file], payload?.file.name, {
        type: "application/xlsx",
      });
      formData.append("venueFile", documentFile);
      formData.append("courseid", payload?.courseid);
      formData.append("ispcm", payload?.ispcm ?? 0);
      formData.append("ispcb", payload?.ispcb ?? 0);
      formData.append("noofattempt", payload?.noofattempt ?? 0);
      let result = yield call(() =>
        axios.post(`${API_URL}/backoffice/venuedata`, formData, { responseType: "blob" })
      );
      // 200: response is a JSON blob — parse it
      const text = yield call(() => result.data.text());
      const jsonData = JSON.parse(text);
      yield put({ type: commonActions.SET_PAGE_LOADER, payload: false });
      yield put({
        type: commonActions.SET_ALERT,
        payload: {
          status: jsonData?.statusCode === 200 ? "success" : "failed",
          show: true,
          message: jsonData?.message,
        },
      });
      if (jsonData?.statusCode === 200) {
        payload.setOpen(false);
        yield put({
          type: venueActions.SET_VENUE_DETAILS,
          payload: jsonData?.data,
        });
      }
      payload.setLoading(false);
      yield delay(2000);
      yield put({
        type: commonActions.SET_ALERT,
        payload: { status: null, show: false, message: null },
      });
    }
  } catch (err) {
    yield put({ type: commonActions.SET_PAGE_LOADER, payload: false });
    payload.setLoading(false);
    yield* handleVenueImportError(err, "Error uploading venue details");
  }
};

// Error handling shared by the venue Excel upload and the VBSA venue import:
// a 400 xlsx body is the validation error file (downloaded), anything else is
// shown as an alert with the server's message.
const handleVenueImportError = function* (err, defaultMessage) {
  const response = err?.response;
  const blob = response?.data;
  // Because the request uses responseType: "blob", every error body arrives
  // as a Blob — the validation .xlsx AND any JSON error body. Sniff the Blob's
  // own type (axios sets it from the response Content-Type) rather than the
  // headers object, which is unreliable on cross-origin/error responses.
  const blobType = blob instanceof Blob ? blob.type || "" : "";
  const isSpreadsheet =
    blobType.includes("spreadsheetml") ||
    blobType.includes("excel") ||
    blobType.includes("ms-excel") ||
    blobType.includes("octet-stream");

  if (response?.status === 400 && blob instanceof Blob && isSpreadsheet) {
    // 400: response is a binary xlsx error file — trigger download
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "venue_upload_errors.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    yield put({
      type: commonActions.SET_ALERT,
      payload: { status: "failed", show: true, message: "Validation errors found. Check the downloaded file." },
    });
  } else {
    // Any other error: the body may still be a Blob (JSON error under
    // responseType: "blob"), so read it as text and try to extract a message.
    let message = defaultMessage;
    if (blob instanceof Blob) {
      try {
        const text = yield call(() => blob.text());
        message = JSON.parse(text)?.message || message;
      } catch (e) {
        // not JSON — keep the default message
      }
    } else if (blob?.message) {
      message = blob.message;
    }
    yield put({
      type: commonActions.SET_ALERT,
      payload: { status: "failed", show: true, message },
    });
  }
  yield delay(2000);
  yield put({
    type: commonActions.SET_ALERT,
    payload: { status: null, show: false, message: null },
  });
};

// Get Venue Data (API): backend fetches the course's venues from VBSA and saves
// them like an upload (replacing the run's venues)
const importVenueDataFromApi = function* (data) {
  const { payload } = data;
  yield put({ type: commonActions.SET_PAGE_LOADER, payload: true });
  try {
    const result = yield call(() =>
      axios.post(
        `${API_URL}/backoffice/venuedata/import-from-api`,
        {
          courseid: payload?.courseid,
          ispcm: payload?.ispcm ?? 0,
          ispcb: payload?.ispcb ?? 0,
          noofattempt: payload?.noofattempt ?? 0,
        },
        { responseType: "blob" }
      )
    );
    const text = yield call(() => result.data.text());
    const jsonData = JSON.parse(text);
    yield put({ type: commonActions.SET_PAGE_LOADER, payload: false });
    yield put({
      type: commonActions.SET_ALERT,
      payload: {
        status: jsonData?.statusCode === 200 ? "success" : "failed",
        show: true,
        message: jsonData?.message,
      },
    });
    if (jsonData?.statusCode === 200) {
      yield put({
        type: venueActions.SET_VENUE_DETAILS,
        payload: jsonData?.data,
      });
    }
    yield delay(2000);
    yield put({
      type: commonActions.SET_ALERT,
      payload: { status: null, show: false, message: null },
    });
  } catch (err) {
    yield put({ type: commonActions.SET_PAGE_LOADER, payload: false });
    yield* handleVenueImportError(err, "Error fetching venue data from VBSA");
  }
};

const getCandidateCount = function* (data) {
  const { payload } = data;
  yield put({ type: commonActions.SET_PAGE_LOADER, payload: true });
  try {
    const result = yield call(() =>
      axios.get(
        `${API_URL}/backoffice/venuedata/getcandidatecount/${payload?.courseid}`,
        { params: { ispcm: payload?.ispcm ?? 0, ispcb: payload?.ispcb ?? 0, noofattempt: payload?.noofattempt ?? 0 } }
      )
    );
    yield put({ type: commonActions.SET_PAGE_LOADER, payload: false });
    yield put({
      type: venueActions.SET_CANDIDATE_COUNT,
      payload: result?.data?.statusCode === 200 ? result?.data?.data : null,
    });
    yield delay(2000);
    yield put({
      type: commonActions.SET_ALERT,
      payload: { status: null, show: false, message: null },
    });
  } catch (err) {
  }
};
const getVenueDetails = function* (data) {
  const { payload } = data;
  yield put({ type: commonActions.SET_PAGE_LOADER, payload: true });
  try {
    const result = yield call(() =>
      axios.get(
        `${API_URL}/backoffice/venuedata/getvenuedetails/${payload?.courseid}`,
        { params: { ispcm: payload?.ispcm ?? 0, ispcb: payload?.ispcb ?? 0, noofattempt: payload?.noofattempt ?? 0 } }
      )
    );
    yield put({ type: commonActions.SET_PAGE_LOADER, payload: false });
    yield put({
      type: venueActions.SET_VENUE_DETAILS,
      payload: result?.data?.data,
    });
    yield delay(2000);
    yield put({
      type: commonActions.SET_ALERT,
      payload: { status: null, show: false, message: null },
    });
  } catch (err) {
  }
};
const getVenueDistrictDetails = function* (data) {
  const { payload } = data;
  yield put({ type: commonActions.SET_PAGE_LOADER, payload: true });
  try {
    const result = yield call(() =>
      axios.get(
        `${API_URL}/backoffice/venuedata/getdistrictvenuedetails/${payload?.courseid}`
      )
    );
    yield put({ type: commonActions.SET_PAGE_LOADER, payload: false });
    yield put({
      type: venueActions.SET_VENUE_DISTRICT_DETAILS,
      payload: {
        all:
          result?.data?.statusCode === 200 ? result?.data?.districtResult : [],
        filtered:
          result?.data?.statusCode === 200 ? result?.data?.districtResult : [],
      },
    });
    yield delay(2000);
    yield put({
      type: commonActions.SET_ALERT,
      payload: { status: null, show: false, message: null },
    });
  } catch (err) {
  }
};
const runSeatAllocation = function* (data) {
  const { payload } = data;
  // yield put({ type: commonActions.SET_PAGE_LOADER, payload: true });
  try {
    // let result = yield call(() => axios.post(`http://localhost:5000/seatallocation/runseatallocation`, { courseid: payload?.courseid, preferenceid: payload?.preferenceid, adminuserid: payload?.adminuserid, status: 1 }))
    let result = yield call(() =>
      axios.post(`${API_URL}/backoffice/venuedata/runseatallocation`, {
        courseid: payload?.courseid,
        preferenceid: payload?.preferenceid,
        adminuserid: payload?.adminuserid,
        pcmb: payload?.pcmb,
        ispcmb: payload?.ispcmb,
        islogicapplied:payload?.isLogicApplied,
        gender:payload?.gender,
        others:payload?.others,
        status: 1,
        ispcm : payload?.ispcm,
        ispcb : payload?.ispcb,
        attempt : payload?.attempt
      })
    );
    // yield put({ type: commonActions.SET_PAGE_LOADER, payload: false });
    // The API answers 202: allocation runs in the background. Progress is shown by the
    // Allocation Status panel, which polls get-allocatevenue-info; the old socket-driven
    // progress modal has nothing left to close it, so it is not opened any more.
    const started = [200, 202].includes(result?.data?.statusCode);
    yield put({
      type: commonActions.SET_ALERT,
      payload: {
        status: started ? "success" : "failed",
        show: true,
        message: result?.data?.message,
      },
    });
    if (started) {
      yield put({
        type: venueActions.GET_ALLOCATION_STATUS,
        payload: {
          courseid: payload?.courseid,
          ispcm: payload?.ispcm,
          ispcb: payload?.ispcb,
          noofattempt: payload?.attempt,
        },
      });
    } else {
      payload?.setStatusModalOpen({
        open: false,
        statustext: "",
      });
    }

    yield delay(2000);
    yield put({
      type: commonActions.SET_ALERT,
      payload: { status: null, show: false, message: null },
    });
  } catch (err) {
    yield put({
      type: commonActions.SET_ALERT,
      payload: {
        status: "failed",
        show: true,
        message: err?.response?.data?.message || "Failed to run seat allocation",
      },
    });
    payload?.setStatusModalOpen({ open: false, statustext: "" });
    yield delay(2000);
    yield put({
      type: commonActions.SET_ALERT,
      payload: { status: null, show: false, message: null },
    });
  }
};
const getVenueseatAllocationDetails = function* (data) {
  // const { payload } = data;
  yield put({ type: commonActions.SET_PAGE_LOADER, payload: true });
  try {
    const result = yield call(() =>
      axios.get(`${API_URL}/backoffice/venuedata/getseatallocationdetails`)
    );
    yield put({ type: commonActions.SET_PAGE_LOADER, payload: false });
    yield put({
      type: venueActions.SET_SEAT_ALLOCATION_DETAILS,
      payload: result?.data?.data,
    });
    yield delay(2000);
    yield put({
      type: commonActions.SET_ALERT,
      payload: { status: null, show: false, message: null },
    });
  } catch (err) {
  }
};
const getSeatAllocation = function* (data) {
  const { payload } = data;
  yield put({ type: commonActions.SET_PAGE_LOADER, payload: true });
  try {
    const result = yield call(() =>
      axios.get(
        `${API_URL}/backoffice/venuedata/getseatallocation/${payload.courseid}`,
        { params: { ispcm: payload?.ispcm ?? 0, ispcb: payload?.ispcb ?? 0, noofattempt: payload?.noofattempt ?? 0 } }
      )
    );
    yield put({ type: commonActions.SET_PAGE_LOADER, payload: false });
    yield put({
      type: venueActions.SET_SEAT_ALLOCATION,
      payload: result?.data?.data,
    });
    yield delay(2000);
    yield put({
      type: commonActions.SET_ALERT,
      payload: { status: null, show: false, message: null },
    });
  } catch (err) {
  }
};

const getCandidateAllocationDetails = function* (data) {
  const { payload } = data;
  try {
    const result = yield call(() =>
      axios.get(
        `${API_URL}/backoffice/venuedata/getcandidateallocationdetails/${payload.courseid}`,
        { params: { ispcm: payload?.ispcm ?? 0, ispcb: payload?.ispcb ?? 0, noofattempt: payload?.noofattempt ?? 0 } }
      )
    );
    yield put({
      type: venueActions.SET_CANDIDATE_ALLOCATION_DEAILS,
      payload: result?.data?.data,
    });
  } catch (err) {
  }
};
const getCategoriesOfCourse  = function*(data){
  const { payload } = data;
  yield put({ type: commonActions.SET_PAGE_LOADER, payload: true });
  try {
    const result = yield call(() =>
      axios.get(
        `${API_URL}/backoffice/venuedata/venue-allocation/getcategories/${payload.courseid}`,
        { params: { ispcm: payload?.ispcm ?? 0, ispcb: payload?.ispcb ?? 0, noofattempt: payload?.noofattempt ?? 0 } }
      )
    );
    yield put({ type: commonActions.SET_PAGE_LOADER, payload: false });
    yield put({
      type: venueActions.SET_CATEGORIES_OF_COURSE,
      payload: result?.data?.data,
    });
    yield delay(2000);
    yield put({
      type: commonActions.SET_ALERT,
      payload: { status: null, show: false, message: null },
    });
  } catch (err) {
  }
}
const resetSeatAllocationDetails = function* (data) {
  const { payload } = data;
  yield put({ type: commonActions.SET_PAGE_LOADER, payload: true });
  try {
    const result = yield call(() =>
      axios.post(`${API_URL}/backoffice/venuedata/resetseatallocation`, payload)
    );
    yield put({
      type: commonActions.SET_ALERT,
      payload: {
        status: result?.data?.statusCode === 200 ? "success" : "failed",
        show: true,
        message: result?.data?.message,
      },
    });
    if (result?.data?.statusCode === 200) {
      // Refresh the same subject group / attempt that was reset.
      const refreshPayload = {
        courseid: payload?.courseid,
        ispcm: payload?.ispcm,
        ispcb: payload?.ispcb,
        noofattempt: payload?.attempt,
      };
      yield put({ type: venueActions.GET_SEAT_ALLOCATION, payload: refreshPayload });
      yield put({ type: venueActions.GET_CANDIDATE_COUNT, payload: refreshPayload });
      yield put({ type: venueActions.GET_VENUE_DETAILS, payload: refreshPayload });
      yield put({ type: venueActions.GET_CANDIDATE_ALLOCATION_DEAILS, payload: refreshPayload });
    }
    yield put({ type: commonActions.SET_PAGE_LOADER, payload: false });
    yield delay(2000);
    yield put({
      type: commonActions.SET_ALERT,
      payload: { status: null, show: false, message: null },
    });
  } catch (err) {
    yield put({ type: commonActions.SET_PAGE_LOADER, payload: false });
    yield put({
      type: commonActions.SET_ALERT,
      payload: {
        status: "failed",
        show: true,
        message: err?.response?.data?.message || "Failed to reset seat allocation",
      },
    });
    yield delay(2000);
    yield put({
      type: commonActions.SET_ALERT,
      payload: { status: null, show: false, message: null },
    });
  }
};

const stopSeatAllocationDetails = function* (data) {
  const { payload } = data;
  yield put({ type: commonActions.SET_PAGE_LOADER, payload: true });
  try {
    const result = yield call(() =>
      axios.post(`${API_URL}/backoffice/venuedata/stopseatallocation`, payload)
    );
    yield put({
      type: commonActions.SET_ALERT,
      payload: {
        status: result?.data?.statusCode === 200 ? "success" : "failed",
        show: true,
        message: result?.data?.message,
      },
    });
    if (result?.data?.statusCode === 200) {
      yield put({ type: venueActions.GET_SEAT_ALLOCATION_DETAILS });
    }

    yield put({ type: commonActions.SET_PAGE_LOADER, payload: false });
    yield delay(2000);
    yield put({
      type: commonActions.SET_ALERT,
      payload: { status: null, show: false, message: null },
    });
  } catch (err) {
  }
};

const uploadOtbsVenue = function* (data) {
  const { payload } = data;
  payload.setLoading(true);
  yield put({ type: commonActions.SET_PAGE_LOADER, payload: true });
  try {
    if (payload?.file && payload?.courseid) {
      let formData = new FormData();
      const documentFile = new File([payload?.file], payload?.file.name, {
        type: "application/xlsx",
      });
      formData.append("otbsFile", documentFile);
      formData.append("courseid", payload?.courseid);
      formData.append("userid", payload?.userid);
      let result = yield call(() =>
        axios.post(`${API_URL}/backoffice/venuedata/otbsbulkupload`, formData)
      );
      yield put({ type: commonActions.SET_PAGE_LOADER, payload: false });
      yield put({
        type: commonActions.SET_ALERT,
        payload: {
          status: result?.data?.statusCode === 200 ? "success" : "failed",
          show: true,
          message: result?.data?.message,
        },
      });
      // if (result?.data?.statusCode === 400) {
      //   payload.setOpen(false);
      //   payload.setLoading(false);
      //   payload.setFile(null)
      //   payload?.setStatusModalOpen({open:true,statustext:

      //     `<h4>
      //     Total Empty venueid : ${result?.data?.data?.venueid} </br>
      //     Total Empty state : ${result?.data?.data?.state} </br>
      //     Total Empty district : ${result?.data?.data?.district } </br>
      //     Total Empty taluka : ${result?.data?.data?.taluka } </br>
      //     Total Empty village : ${result?.data?.data?.village } </br>
      //     Total Empty seatsavailability : ${result?.data?.data?.seatsavailability } </br>
      //     Total Empty addressline1 : ${result?.data?.data?.addressline1 } </br>
      //     Total Empty addressline2 : ${result?.data?.data?.addressline2 } </br>
      //     Total Empty buildingtype : ${result?.data?.data?.buildingtype } </br>
      //     Total Empty city : ${result?.data?.data?.city } </br>
      //     Total Empty googlemaplink : ${result?.data?.data?.googlemaplink } </br>
      //     Total Empty landmark : ${result?.data?.data?.landmark } </br>
      //     Total Empty pincode : ${result?.data?.data?.pincode } </br>
      //     Total Empty venuename : ${result?.data?.data?.venuename } </br>
      //     Total Empty venuetype : ${result?.data?.data?.venuetype } </br>
      //     Total Empty suitableforpwd : ${result?.data?.data?.suitableforpwd } </br>

      //     </h4>`})
      //   // yield put({
      //   //   type: venueActions.OTBS_SET_VENUE,
      //   //   payload: result?.data?.venueDetails,
      //   // });
      // }
      if (result?.data?.statusCode === 400) {
        payload.setOpen(false);
        payload.setLoading(false);
        payload.setFile(null);

        let statustext = "<h4>";

        if (result?.data?.data?.venueid > 0) {
          statustext += `Total Empty venueid : ${result?.data?.data?.venueid} </br>`;
        }

        if (result?.data?.data?.state > 0) {
          statustext += `Total Empty state : ${result?.data?.data?.state} </br>`;
        }

        if (result?.data?.data?.district > 0) {
          statustext += `Total Empty district : ${result?.data?.data?.district} </br>`;
        }

        if (result?.data?.data?.taluka > 0) {
          statustext += `Total Empty taluka : ${result?.data?.data?.taluka} </br>`;
        }

        if (result?.data?.data?.village > 0) {
          statustext += `Total Empty village : ${result?.data?.data?.village} </br>`;
        }

        if (result?.data?.data?.seatsavailability > 0) {
          statustext += `Total Empty seatsavailability : ${result?.data?.data?.seatsavailability} </br>`;
        }

        if (result?.data?.data?.addressline1 > 0) {
          statustext += `Total Empty addressline1 : ${result?.data?.data?.addressline1} </br>`;
        }

        if (result?.data?.data?.addressline2 > 0) {
          statustext += `Total Empty addressline2 : ${result?.data?.data?.addressline2} </br>`;
        }

        if (result?.data?.data?.buildingtype > 0) {
          statustext += `Total Empty buildingtype : ${result?.data?.data?.buildingtype} </br>`;
        }

        if (result?.data?.data?.city > 0) {
          statustext += `Total Empty city : ${result?.data?.data?.city} </br>`;
        }

        if (result?.data?.data?.googlemaplink > 0) {
          statustext += `Total Empty googlemaplink : ${result?.data?.data?.googlemaplink} </br>`;
        }

        if (result?.data?.data?.landmark > 0) {
          statustext += `Total Empty landmark : ${result?.data?.data?.landmark} </br>`;
        }

        if (result?.data?.data?.pincode > 0) {
          statustext += `Total Empty pincode : ${result?.data?.data?.pincode} </br>`;
        }

        if (result?.data?.data?.venuename > 0) {
          statustext += `Total Empty venuename : ${result?.data?.data?.venuename} </br>`;
        }

        if (result?.data?.data?.venuetype > 0) {
          statustext += `Total Empty venuetype : ${result?.data?.data?.venuetype} </br>`;
        }

        if (result?.data?.data?.suitableforpwd > 0) {
          statustext += `Total Empty suitableforpwd : ${result?.data?.data?.suitableforpwd} </br>`;
        }

        statustext += "</h4>";

        if (statustext !== "<h4></h4>") {
          payload?.setStatusModalOpen({
            open: true,
            statustext: statustext,
          });
        }
      } else {
        payload.setLoading(false);
      }
      if (result?.data?.statusCode === 200) {
        payload.setOpen(false);
        payload.setLoading(false);
        payload.setFile(null);
        payload?.setStatusModalOpen({
          open: true,
          statustext: `<h4>
          Total Uploaded Venues : ${
            result?.data?.duplicatesCount + result?.data?.uniqueCount
          } </br>
          Total Inserted Venues : ${result?.data?.uniqueCount} </br>
          Total Existing Venues : ${result?.data?.duplicatesCount} </br>
          </h4>`,
        });
        yield put({
          type: venueActions.OTBS_SET_VENUE,
          payload: result?.data?.venueDetails,
        });
      } else {
        payload.setLoading(false);
      }

      yield delay(2000);
      yield put({
        type: commonActions.SET_ALERT,
        payload: { status: null, show: false, message: null },
      });
    }
  } catch (err) {
  }
};

const getOtbsVenue = function* (data) {
  const { payload } = data;
  yield put({ type: commonActions.SET_PAGE_LOADER, payload: true });
  try {
    const result = yield call(() =>
      axios.get(`${API_URL}/backoffice/venuedata/${payload.courseid}`)
    );
    yield put({ type: commonActions.SET_PAGE_LOADER, payload: false });
    yield put({
      type: venueActions.OTBS_SET_VENUE,
      payload: result?.data?.statusCode === 200 ? result?.data?.result : [],
    });
    yield delay(2000);
    yield put({
      type: commonActions.SET_ALERT,
      payload: { status: null, show: false, message: null },
    });
  } catch (err) {
  }
};

const updateOtbsVenue = function* (data) {
  const { payload } = data;
  yield put({ type: commonActions.SET_PAGE_LOADER, payload: true });
  try {
    const result = yield call(() =>
      axios.put(
        `${API_URL}/backoffice/venuedata/edit/${payload?.courseid}/${payload?.venueid}`,
        {
          totalseats: payload?.totalseats,
        }
      )
    );
    yield put({ type: commonActions.SET_PAGE_LOADER, payload: false });
    if (result?.data?.statusCode === 200) {
      yield put({
        type: venueActions.OTBS_GET_VENUE,
        payload: { courseid: payload?.courseid },
      });
      payload?.setEditmodal({
        open: false,
        data: null,
      });
    }

    yield delay(2000);
    yield put({
      type: commonActions.SET_ALERT,
      payload: { status: null, show: false, message: null },
    });
  } catch (err) {
  }
};

const getSeatAllocationStatus = function* (data) {
  const { payload } = data;
  try {
    const result = yield call(() =>
      axios.get(
        `${API_URL}/backoffice/venuedata/get-allocatevenue-info/${payload.courseid}`,
        { params: { ispcm: payload?.ispcm ?? 0, ispcb: payload?.ispcb ?? 0, noofattempt: payload?.noofattempt ?? 0 } }
      )
    );
    yield put({
      type: venueActions.SET_ALLOCATION_STATUS,
      payload: result?.data?.data,
    });
    yield delay(2000);
  } catch (err) {
  }
};

// ─── Duplicate candidates ────────────────────────────────────────────────────
// Candidates with the same name, father, mother, DOB and spouse are seated in the
// same venue and batch. Seat allocation refuses to run until the course's
// duplicates have been refreshed after its last payment.

const DUPLICATES_URL = `${API_URL}/backoffice/venuedata/duplicate-candidates`;

const showAlert = function* (status, message) {
  yield put({ type: commonActions.SET_ALERT, payload: { status, show: true, message } });
  yield delay(2000);
  yield put({ type: commonActions.SET_ALERT, payload: { status: null, show: false, message: null } });
};

const getDuplicateSummary = function* ({ payload }) {
  try {
    const result = yield call(() => axios.get(`${DUPLICATES_URL}/${payload.courseid}/summary`));
    yield put({
      type: venueActions.SET_DUPLICATE_SUMMARY,
      payload: { courseid: payload.courseid, summary: result?.data?.result || null },
    });
  } catch (err) {
    yield put({
      type: venueActions.SET_DUPLICATE_SUMMARY,
      payload: { courseid: payload.courseid, summary: null },
    });
  }
};

const refreshDuplicates = function* ({ payload }) {
  payload?.setLoading?.(true);
  try {
    const result = yield call(() =>
      axios.post(`${DUPLICATES_URL}/refresh`, { courseid: payload.courseid, userid: payload.userid })
    );
    const r = result?.data?.result;
    yield put({ type: venueActions.GET_DUPLICATE_SUMMARY, payload: { courseid: payload.courseid } });
    payload?.setLoading?.(false);
    yield* showAlert(
      "success",
      `Duplicates refreshed for course ${payload.courseid}: ${r?.groupcount ?? 0} group(s), ${r?.candidatecount ?? 0} candidate(s)`
    );
  } catch (err) {
    payload?.setLoading?.(false);
    yield* showAlert("failed", err?.response?.data?.message || "Failed to refresh duplicate candidates");
  }
};

const getDuplicateGroups = function* ({ payload }) {
  try {
    const result = yield call(() =>
      axios.get(`${DUPLICATES_URL}/${payload.courseid}`, {
        params: { page: payload.page || 1, size: payload.size || 20 },
      })
    );
    yield put({
      type: venueActions.SET_DUPLICATE_GROUPS,
      payload: result?.data?.result || { total: 0, page: 1, size: 20, groups: [] },
    });
  } catch (err) {
    yield put({ type: venueActions.SET_DUPLICATE_GROUPS, payload: { total: 0, page: 1, size: 20, groups: [] } });
    yield* showAlert("failed", err?.response?.data?.message || "Failed to load duplicate candidates");
  }
};

const reviewDuplicateGroup = function* ({ payload }) {
  try {
    const result = yield call(() =>
      axios.put(`${DUPLICATES_URL}/review`, {
        courseid: payload.courseid,
        groupkey: payload.groupkey,
        decision: payload.decision,
        remarks: payload.remarks,
        userid: payload.userid,
      })
    );
    yield put({
      type: venueActions.GET_DUPLICATE_GROUPS,
      payload: { courseid: payload.courseid, page: payload.page, size: payload.size },
    });
    yield put({ type: venueActions.GET_DUPLICATE_SUMMARY, payload: { courseid: payload.courseid } });
    yield* showAlert("success", result?.data?.message);
  } catch (err) {
    yield* showAlert("failed", err?.response?.data?.message || "Failed to save review");
  }
};

const getDuplicateReport = function* ({ payload }) {
  try {
    const result = yield call(() =>
      axios.get(`${DUPLICATES_URL}/${payload.courseid}/allocation-report`, {
        params: { ispcm: payload?.ispcm ?? 0, ispcb: payload?.ispcb ?? 0, attempt: payload?.attempt ?? 0 },
      })
    );
    yield put({
      type: venueActions.SET_DUPLICATE_REPORT,
      payload: result?.data?.result || { counts: {}, groups: [] },
    });
  } catch (err) {
    yield put({ type: venueActions.SET_DUPLICATE_REPORT, payload: { counts: {}, groups: [] } });
    yield* showAlert("failed", err?.response?.data?.message || "Failed to load duplicate allocation report");
  }
};

export default venueSagas;
